import {
  CertificateElement,
  CertificatePageSettings,
  CertificateTemplate,
} from "./certificateTypes";

export const DEFAULT_CERTIFICATE_LOGO =
  "data:image/svg+xml;charset=UTF-8," +
  encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 80" fill="none">
  <rect width="80" height="80" rx="16" fill="#EEF2FF"/>
  <path d="M40 14c-7 8-16 12-16 26 0 10 7 18 16 26 9-8 16-16 16-26 0-14-9-18-16-26z" fill="#4F46E5"/>
  <path d="M40 22c-4 6-9 9-9 18 0 7 4 12 9 18 5-6 9-11 9-18 0-9-5-12-9-18z" fill="#FFFFFF" opacity=".9"/>
</svg>`);

const PAGE: CertificatePageSettings = {
  size: "A4",
  orientation: "portrait",
  marginMm: 20,
  fontFamily: "Georgia, 'Times New Roman', serif",
  fontSize: 12,
  fontWeight: 400,
  lineHeight: 1.45,
  textAlign: "center",
  logoSrc: DEFAULT_CERTIFICATE_LOGO,
  logoSizeMm: 28,
  fieldSpacing: 4,
  paragraphSpacing: 6,
  labelValueStyle: "inline",
  showSafeArea: true,
  headerAlign: "center",
  footerText: "Thank You!",
};

const text = (
  partial: Omit<CertificateElement, "type" | "visible" | "lineHeight"> & {
    visible?: boolean;
    lineHeight?: number;
  }
): CertificateElement => ({
  type: "text",
  visible: true,
  lineHeight: 1.4,
  ...partial,
});

function standardElements(body: {
  title: string;
  treatmentLine: string;
  leaveLine: string;
}): CertificateElement[] {
  return [
    {
      id: "logo",
      type: "logo",
      label: "Clinic logo",
      section: "header",
      x: 91,
      y: 18,
      width: 28,
      height: 28,
      content: "",
      visible: true,
      fontSize: 12,
      fontWeight: 400,
      align: "center",
      lineHeight: 1,
    },
    text({
      id: "clinicName",
      label: "Clinic name",
      section: "header",
      x: 20,
      y: 50,
      width: 170,
      height: 10,
      content: "{{clinicName}}",
      fontSize: 16,
      fontWeight: 700,
      align: "center",
      letterSpacing: 1.4,
      textTransform: "uppercase",
      color: "#0f172a",
      fieldKey: "clinicName",
    }),
    text({
      id: "clinicAddress",
      label: "Clinic address",
      section: "header",
      x: 20,
      y: 62,
      width: 170,
      height: 7,
      content: "{{clinicAddress}}",
      fontSize: 10,
      fontWeight: 400,
      align: "center",
      color: "#334155",
      fieldKey: "clinicAddress",
    }),
    text({
      id: "clinicContact",
      label: "Contact information",
      section: "header",
      x: 20,
      y: 70,
      width: 170,
      height: 6,
      content: "Telephone: {{clinicPhone}}  |  WhatsApp: {{clinicWhatsapp}}",
      fontSize: 9,
      fontWeight: 400,
      align: "center",
      color: "#475569",
    }),
    text({
      id: "clinicWeb",
      label: "Website / Email",
      section: "header",
      x: 20,
      y: 77,
      width: 170,
      height: 6,
      content: "{{clinicWebsite}}  |  {{clinicEmail}}",
      fontSize: 9,
      fontWeight: 400,
      align: "center",
      color: "#475569",
    }),
    text({
      id: "title",
      label: "Certificate title",
      section: "body",
      x: 20,
      y: 94,
      width: 170,
      height: 12,
      content: body.title,
      fontSize: 17,
      fontWeight: 700,
      align: "center",
      letterSpacing: 3.2,
      textTransform: "uppercase",
      color: "#1e3a8a",
    }),
    text({
      id: "certificateNumber",
      label: "Certificate number",
      section: "body",
      x: 20,
      y: 114,
      width: 90,
      height: 7,
      content: "Certificate No: {{certificateNumber}}",
      fontSize: 10,
      fontWeight: 500,
      align: "left",
      color: "#334155",
      fieldKey: "certificateNumber",
    }),
    text({
      id: "certificateDate",
      label: "Certificate date",
      section: "body",
      x: 100,
      y: 114,
      width: 90,
      height: 7,
      content: "Date: {{certificateDate}}",
      fontSize: 10,
      fontWeight: 500,
      align: "right",
      color: "#334155",
      fieldKey: "certificateDate",
    }),
    text({
      id: "intro",
      label: "Certification intro",
      section: "body",
      x: 20,
      y: 132,
      width: 170,
      height: 8,
      content: "This is to certify that",
      fontSize: 12,
      fontWeight: 400,
      align: "center",
      color: "#1e293b",
    }),
    text({
      id: "patientName",
      label: "Patient name",
      section: "body",
      x: 20,
      y: 144,
      width: 170,
      height: 12,
      content: "{{patientName}}",
      fontSize: 22,
      fontWeight: 700,
      align: "center",
      letterSpacing: 2,
      textTransform: "uppercase",
      color: "#0f172a",
      fieldKey: "patientName",
    }),
    text({
      id: "patientDetails",
      label: "Patient details",
      section: "body",
      x: 20,
      y: 157,
      width: 170,
      height: 6,
      content: "Patient ID: {{patientId}}  ·  Age: {{patientAge}}  ·  {{patientGender}}",
      fontSize: 9,
      fontWeight: 400,
      align: "center",
      color: "#64748b",
      fieldKey: "patientId",
    }),
    text({
      id: "treatmentType",
      label: "Treatment type",
      section: "body",
      x: 20,
      y: 170,
      width: 170,
      height: 8,
      content: body.treatmentLine,
      fontSize: 12,
      fontWeight: 400,
      align: "center",
      color: "#1e293b",
      fieldKey: "treatmentType",
    }),
    text({
      id: "treatmentDescription",
      label: "Treatment description",
      section: "body",
      x: 20,
      y: 179,
      width: 170,
      height: 8,
      content: "Treatment date: {{treatmentDate}}",
      fontSize: 11,
      fontWeight: 400,
      align: "center",
      color: "#334155",
      fieldKey: "treatmentDescription",
    }),
    text({
      id: "reason",
      label: "Reason",
      section: "body",
      x: 20,
      y: 192,
      width: 170,
      height: 10,
      content: "Reason: {{reasonForLeave}}",
      fontSize: 11,
      fontWeight: 400,
      align: "center",
      color: "#1e293b",
      fieldKey: "reasonForLeave",
    }),
    text({
      id: "leaveDates",
      label: "Leave dates",
      section: "body",
      x: 20,
      y: 206,
      width: 170,
      height: 14,
      content: body.leaveLine,
      fontSize: 12,
      fontWeight: 500,
      align: "center",
      lineHeight: 1.55,
      color: "#1e293b",
      fieldKey: "leaveStartDate",
    }),
    text({
      id: "recommendationText",
      label: "Recommendation text",
      section: "body",
      x: 28,
      y: 226,
      width: 154,
      height: 12,
      content: "{{recommendationText}}",
      fontSize: 10,
      fontWeight: 400,
      align: "center",
      color: "#475569",
      fieldKey: "recommendationText",
    }),
    text({
      id: "thankYou",
      label: "Footer text",
      section: "footer",
      x: 20,
      y: 244,
      width: 170,
      height: 8,
      content: "Thank You!",
      fontSize: 13,
      fontWeight: 600,
      align: "center",
      color: "#1e3a8a",
    }),
    text({
      id: "signature",
      label: "Doctor signature",
      section: "footer",
      x: 118,
      y: 258,
      width: 72,
      height: 8,
      content: "____________________",
      fontSize: 12,
      fontWeight: 400,
      align: "right",
      color: "#0f172a",
    }),
    text({
      id: "doctorName",
      label: "Doctor name",
      section: "footer",
      x: 118,
      y: 266,
      width: 72,
      height: 6,
      content: "{{doctorName}}",
      fontSize: 10,
      fontWeight: 700,
      align: "right",
      color: "#0f172a",
      fieldKey: "doctorName",
    }),
    text({
      id: "qualifications",
      label: "Qualifications",
      section: "footer",
      x: 118,
      y: 272,
      width: 72,
      height: 6,
      content: "{{doctorQualifications}}",
      fontSize: 8,
      fontWeight: 400,
      align: "right",
      color: "#475569",
      fieldKey: "doctorQualifications",
    }),
    text({
      id: "designation",
      label: "Designation",
      section: "footer",
      x: 118,
      y: 278,
      width: 72,
      height: 5,
      content: "{{doctorDesignation}}",
      fontSize: 8,
      fontWeight: 500,
      align: "right",
      color: "#334155",
      fieldKey: "doctorDesignation",
    }),
    text({
      id: "slmcNumber",
      label: "SLMC registration number",
      section: "footer",
      x: 118,
      y: 283,
      width: 72,
      height: 5,
      content: "SLMC Reg No. {{slmcNumber}}",
      fontSize: 8,
      fontWeight: 400,
      align: "right",
      color: "#475569",
      fieldKey: "slmcNumber",
    }),
  ];
}

function cloneTemplate(
  id: string,
  name: string,
  body: { title: string; treatmentLine: string; leaveLine: string }
): CertificateTemplate {
  return {
    id,
    name,
    page: { ...PAGE },
    elements: standardElements(body),
  };
}

export const DEFAULT_CERTIFICATE_TEMPLATES: CertificateTemplate[] = [
  cloneTemplate("standard-dental", "Standard Dental Certificate", {
    title: "Medical Certificate",
    treatmentLine: "is undergoing {{treatmentDescription}}.",
    leaveLine:
      "I recommend {{leaveDays}} days of leave from\n{{leaveStartDate}} to {{leaveEndDate}}.",
  }),
  cloneTemplate("standard-medical", "Standard Medical Certificate", {
    title: "Medical Certificate",
    treatmentLine: "is receiving {{treatmentType}}.",
    leaveLine:
      "Leave is recommended for {{leaveDays}} days\nfrom {{leaveStartDate}} to {{leaveEndDate}}.",
  }),
  cloneTemplate("orthodontic", "Orthodontic Certificate", {
    title: "Orthodontic Certificate",
    treatmentLine: "is undergoing {{treatmentType}}.",
    leaveLine:
      "I recommend {{leaveDays}} days of leave from\n{{leaveStartDate}} to {{leaveEndDate}}.",
  }),
  cloneTemplate("dental-surgery", "Dental Surgery Certificate", {
    title: "Dental Surgery Certificate",
    treatmentLine: "has undergone / is scheduled for {{treatmentType}}.",
    leaveLine:
      "Post-operative leave of {{leaveDays}} days is advised\nfrom {{leaveStartDate}} to {{leaveEndDate}}.",
  }),
  cloneTemplate("leave", "Leave Certificate", {
    title: "Leave Certificate",
    treatmentLine: "requires leave in relation to {{treatmentType}}.",
    leaveLine:
      "Leave is certified from {{leaveStartDate}} to {{leaveEndDate}}\n({{leaveDays}} days).",
  }),
];

export const DEFAULT_TEMPLATE_ID = DEFAULT_CERTIFICATE_TEMPLATES[0].id;

export function cloneCertificateTemplate(
  template: CertificateTemplate,
  next: { id: string; name: string }
): CertificateTemplate {
  return {
    id: next.id,
    name: next.name,
    page: { ...template.page },
    elements: template.elements.map((el) => ({ ...el })),
  };
}

export function getDefaultTemplateById(id: string): CertificateTemplate | undefined {
  const found = DEFAULT_CERTIFICATE_TEMPLATES.find((item) => item.id === id);
  return found
    ? cloneCertificateTemplate(found, { id: found.id, name: found.name })
    : undefined;
}
